import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, inpatient } from '../lib/api';
import { CreateDoctorStatusEventPayload } from '../types';

// Helper function to handle nested data structure from backend
// Backend may return {data: [...]} which fetchApi wraps in {data: ...}
export function extractData<T>(response: { data?: T | { data: T } }): T | null {
  if (!response.data) return null;
  const data = response.data as Record<string, unknown> | T;
  if (data && typeof data === 'object' && 'data' in data) {
    return (data as Record<string, unknown>).data as T;
  }
  return data as T;
}

// Query Keys - centralized for consistency
export const queryKeys = {
  doctors: {
    all: ['doctors'] as const,
    active: ['doctors', 'active'] as const,
  },
  patients: {
    all: ['patients'] as const,
    byId: (id: string) => ['patients', id] as const,
    search: (query: string) => ['patients', 'search', query] as const,
    visits: (patientId: string) => ['patients', patientId, 'visits'] as const,
  },
  appointments: {
    all: ['appointments'] as const,
    byDate: (date: string, doctorId?: string) => 
      ['appointments', 'date', date, doctorId] as const,
    today: (doctorId: string) => ['appointments', 'today', doctorId] as const,
  },
  schedule: {
    all: ['schedule'] as const,
    byDoctor: (doctorId: string, date: string) => 
      ['schedule', 'doctor', doctorId, date] as const,
  },
  doctorStatus: {
    byDoctor: (doctorId: string, date: string) => 
      ['doctorStatus', doctorId, date] as const,
    current: (doctorId: string) => ['doctorStatus', 'current', doctorId] as const,
  },
  inpatient: {
    active: ['inpatient', 'active'] as const,
    wards: ['inpatient', 'wards'] as const,
    beds: (wardId: string) => ['inpatient', 'beds', wardId] as const,
    rounds: (admissionId: string) => ['inpatient', admissionId, 'rounds'] as const,
  },
  revenue: {
    summary: ['revenue', 'summary'] as const,
    today: ['revenue', 'today'] as const,
    week: ['revenue', 'week'] as const,
    month: ['revenue', 'month'] as const,
    range: (startDate: string, endDate: string) => ['revenue', 'range', startDate, endDate] as const,
    paymentMethods: (startDate: string, endDate: string) => ['revenue', 'methods', startDate, endDate] as const,
  },
};

// ==================== DOCTORS ====================

export function useDoctors() {
  return useQuery({
    queryKey: queryKeys.doctors.all,
    queryFn: async () => {
      const response = await api.doctors.getAll();
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
  });
}

export function useActiveDoctors() {
  const { data: allDoctors, ...rest } = useDoctors();
  const activeDoctors = allDoctors?.filter((d) => d.isActive) || [];
  
  return {
    data: activeDoctors,
    ...rest,
  };
}

// ==================== PATIENTS ====================

export function useAllPatients() {
  return useQuery({
    queryKey: queryKeys.patients.all,
    queryFn: async () => {
      const response = await api.patients.getAll();
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
    enabled: false, // Don't auto-fetch, trigger manually
  });
}

export function usePatientById(id: string | null) {
  return useQuery({
    queryKey: queryKeys.patients.byId(id!),
    queryFn: async () => {
      const response = await api.patients.getById(id!);
      if (response.error) throw new Error(response.error);
      return extractData(response);
    },
    enabled: !!id,
  });
}

export function usePatientSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.patients.search(query),
    queryFn: async () => {
      const response = await api.patients.search(query);
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
    enabled: query.length >= 3,
  });
}
export function useCreatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await api.patients.create(data);
      if (response.error) throw new Error(response.error);
      return extractData(response);
    },
    onSuccess: () => {
      // Invalidate ALL patient-related queries (all, search, visits, etc.)
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}
export function usePatientVisits(patientId: string | null) {
  return useQuery({
    queryKey: queryKeys.patients.visits(patientId!),
    queryFn: async () => {
      const response = await api.patients.getVisits(patientId!);
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
    enabled: !!patientId,
  });
}

// ==================== APPOINTMENTS ====================

export function useAppointmentsByDate(date: string, doctorId: string | null) {
  return useQuery({
    queryKey: queryKeys.appointments.byDate(date, doctorId || undefined),
    queryFn: async () => {
      const response = await api.appointments.getAll(date, doctorId || undefined);
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
    enabled: !!doctorId && !!date,
  });
}

export function useTodayAppointments(doctorId: string | null) {
  const today = new Date().toISOString().split('T')[0];
  return useAppointmentsByDate(today, doctorId);
}

// ==================== APPOINTMENTS ====================

export function useAppointments(date?: string, doctorId?: string) {
  return useQuery({
    queryKey: queryKeys.appointments.byDate(date || '', doctorId),
    queryFn: async () => {
      const response = await api.appointments.getAll(date, doctorId);
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
    enabled: !!date,
  });
}

// ==================== INPATIENT ====================

export function useActiveInpatients() {
  return useQuery({
    queryKey: queryKeys.inpatient.active,
    queryFn: async () => {
      const response = await inpatient.getActiveRecords();
      if (response.error) throw new Error(response.error);
      const data = extractData<any[]>(response);
      const admissions = Array.isArray(data) ? data : [];

      // The Admission entity's `patient` relation is @JsonIgnore'd server-side (deliberately — it's
      // the decrypted record, and Admission is returned raw from InpatientController with no DTO
      // layer), so `admission.patient` is always undefined straight off this endpoint. Every screen
      // that renders a patient's name from an admission (census cards, clinical dashboard, round
      // history) expects `admission.patient` to be populated, so resolve real identity here, once,
      // through the same authorized bulk endpoint the front-desk already uses (api.patients.getByIds
      // -> POST /api/patients/batch, clinic-scoped) rather than falling back to "Unknown Patient" or
      // ever rendering the raw patientId.
      const patientIds = Array.from(new Set(admissions.map((a) => a.patientId).filter(Boolean)));
      if (patientIds.length === 0) return admissions;

      const patientsResponse = await api.patients.getByIds(patientIds);
      const patients = Array.isArray(patientsResponse.data) ? patientsResponse.data : [];
      const byId = new Map(patients.map((p) => [p.id, p]));

      return admissions.map((a) => ({ ...a, patient: byId.get(a.patientId) ?? a.patient }));
    },
  });
}

export function useWards() {
  return useQuery({
    queryKey: queryKeys.inpatient.wards,
    queryFn: async () => {
      const response = await inpatient.getWards();
      if (response.error) throw new Error(response.error);
      return extractData<any[]>(response) || [];
    },
  });
}

export function useBeds(wardId: string) {
  return useQuery({
    queryKey: queryKeys.inpatient.beds(wardId),
    queryFn: async () => {
      const response = await inpatient.getBeds(wardId);
      if (response.error) throw new Error(response.error);
      return extractData<any[]>(response) || [];
    },
    enabled: !!wardId,
  });
}

export function useCreateWard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await inpatient.createWard(data);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inpatient.wards });
    },
  });
}

export function useAdmitPatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await inpatient.admit(data);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inpatient.active });
      queryClient.invalidateQueries({ queryKey: queryKeys.inpatient.wards });
      // Invalidate beds query for the ward to refresh bed occupancy status
      if (variables.wardId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.inpatient.beds(variables.wardId) });
      }
    },
  });
}

export function useRecordWardRound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await inpatient.recordRound(data);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inpatient.active });
    },
  });
}

export function useSchedule(doctorId: string, date: string) {
  return useQuery({
    queryKey: queryKeys.schedule.byDoctor(doctorId, date),
    queryFn: async () => {
      const response = await api.schedule.getSchedule(doctorId, date);
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
    enabled: !!doctorId && !!date,
  });
}

export function useDoctorStatusEvents(doctorId?: string, date?: string) {
  return useQuery({
    queryKey: queryKeys.doctorStatus.byDoctor(doctorId || '', date || ''),
    queryFn: async () => {
      const response = await api.doctorStatus.list(doctorId, date, date);
      if (response.error) throw new Error(response.error);
      const responseData = extractData(response);
      return Array.isArray(responseData) ? responseData : [];
    },
    enabled: !!doctorId && !!date,
  });
}

export function useCurrentDoctorStatus(doctorId?: string) {
  return useQuery({
    queryKey: queryKeys.doctorStatus.current(doctorId || ''),
    queryFn: async () => {
      const response = await api.doctorStatus.current(doctorId);
      if (response.error) throw new Error(response.error);
      return extractData(response);
    },
    enabled: !!doctorId,
    staleTime: 60_000,
  });
}

// ==================== MUTATIONS ====================

export function useCreateScheduleSlot() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Parameters<typeof api.schedule.createSlot>[0]) => {
      const response = await api.schedule.createSlot(data);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedule.byDoctor(variables.doctorId, variables.date) });
    },
  });
}

export function useBlockSlot() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ slotId, reason }: { slotId: string, reason?: string }) => {
      const response = await api.schedule.blockSlot(slotId, reason);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedule.all });
    },
  });
}

export function useCreateDoctorStatusEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDoctorStatusEventPayload) => {
      const response = await api.doctorStatus.create(payload);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (_, variables) => {
      const dateKey = variables.startTime ? variables.startTime.slice(0, 10) : '';
      if (dateKey) {
        queryClient.invalidateQueries({ queryKey: queryKeys.doctorStatus.byDoctor(variables.doctorId, dateKey) });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.doctorStatus.current(variables.doctorId) });
    },
  });
}

export function useUnblockSlot() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ slotId }: { slotId: string }) => {
      const response = await api.schedule.unblockSlot(slotId);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedule.all });
    },
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (appointmentData: Parameters<typeof api.appointments.create>[0]) => {
      const response = await api.appointments.create(appointmentData);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all appointment queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
    },
  });
}

export function useUpdateAppointmentStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Parameters<typeof api.appointments.updateStatus>[1] }) => {
      const response = await api.appointments.updateStatus(id, status);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch appointment queries immediately
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
      queryClient.refetchQueries({ queryKey: queryKeys.appointments.all });
    },
  });
}

export function useCreateDoctor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (doctorData: Parameters<typeof api.doctors.create>[0]) => {
      const response = await api.doctors.create(doctorData);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.doctors.all });
    },
  });
}

export function useUpdateDoctor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Parameters<typeof api.doctors.update>[1] }) => {
      const response = await api.doctors.update(id, data);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.doctors.all });
    },
  });
}

// ==================== REVENUE HOOKS ====================

export function useRevenueSummary() {
  return useQuery({
    queryKey: queryKeys.revenue.summary,
    queryFn: async () => {
      const response = await api.bills.getRevenueSummary();
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useTodayRevenue() {
  return useQuery({
    queryKey: queryKeys.revenue.today,
    queryFn: async () => {
      const response = await api.bills.getTodayRevenue();
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useWeekRevenue() {
  return useQuery({
    queryKey: queryKeys.revenue.week,
    queryFn: async () => {
      const response = await api.bills.getWeekRevenue();
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useMonthRevenue() {
  return useQuery({
    queryKey: queryKeys.revenue.month,
    queryFn: async () => {
      const response = await api.bills.getMonthRevenue();
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
}

export function useRevenueByDateRange(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: queryKeys.revenue.range(startDate || '', endDate || ''),
    queryFn: async () => {
      if (!startDate || !endDate) throw new Error('Start and end dates are required');
      const response = await api.bills.getRevenueByDateRange(startDate, endDate);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    enabled: !!startDate && !!endDate,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function usePaymentMethodBreakdown(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: queryKeys.revenue.paymentMethods(startDate || '', endDate || ''),
    queryFn: async () => {
      if (!startDate || !endDate) throw new Error('Start and end dates are required');
      const response = await api.bills.getPaymentMethodBreakdown(startDate, endDate);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    enabled: !!startDate && !!endDate,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
